import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Lightbox from '../components/portal/Lightbox';
import { fetchPortalReport, PortalLinkError } from '../services/portal';
import './ClientReportPage.css';

/**
 * The report as its recipient reads it, through the unique link sent with
 * the PDF. No account: the token in the URL is the access. The photos are the
 * point — every figure opens full screen, with zoom.
 */

const FONTS_HREF = 'https://fonts.googleapis.com/css2?family=IBM+Plex+Sans+Condensed:wght@400;500;600&family=Source+Serif+4:ital,opsz,wght@0,8..60,400;0,8..60,600;1,8..60,400&display=swap';
const FALLBACK_ACCENT = '#0f5e73';

function usePortalHead(title) {
  useEffect(() => {
    // The report must never be indexed, even if a link leaks.
    const robots = document.createElement('meta');
    robots.name = 'robots';
    robots.content = 'noindex, nofollow';
    document.head.appendChild(robots);
    let fonts = document.querySelector('link[data-portal-fonts]');
    if (!fonts) {
      fonts = document.createElement('link');
      fonts.rel = 'stylesheet';
      fonts.href = FONTS_HREF;
      fonts.setAttribute('data-portal-fonts', '');
      document.head.appendChild(fonts);
    }
    return () => robots.remove();
  }, []);
  useEffect(() => {
    if (title) document.title = title;
  }, [title]);
}

function formatDate(value, lang) {
  if (!value) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!m) return value;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return d.toLocaleDateString(lang === 'en' ? 'en-GB' : 'fr-BE', { day: 'numeric', month: 'long', year: 'numeric' });
}

const isHex = (c) => /^#[0-9a-f]{6}$/i.test(c || '');

function Party({ party }) {
  return (
    <>
      {party.name}
      {party.contact_person && <span className="p-meta-sub">{party.contact_person}</span>}
      {party.address && <span className="p-meta-sub">{party.address}</span>}
    </>
  );
}

function Rich({ html, className = '' }) {
  // Server-rendered from Markdown after HTML-escaping the text (see
  // backend services/markdown_text.py): no markup from the report survives.
  // eslint-disable-next-line react/no-danger
  return <div className={`p-rich ${className}`.trim()} dangerouslySetInnerHTML={{ __html: html }} />;
}

export default function ClientReportPage() {
  const { token } = useParams();
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'fr').startsWith('en') ? 'en' : 'fr';
  const [report, setReport] = useState(null);
  const [state, setState] = useState('loading'); // loading | ready | invalid | error
  const [open, setOpen] = useState(null); // { index, rect }

  const load = useCallback(() => {
    setState('loading');
    fetchPortalReport(token)
      .then((data) => { setReport(data); setState('ready'); })
      .catch((err) => setState(err instanceof PortalLinkError ? 'invalid' : 'error'));
  }, [token]);

  useEffect(load, [load]);

  const figures = useMemo(
    () => (report?.steps || []).flatMap((s) => s.figures),
    [report],
  );
  const positionOf = useMemo(
    () => new Map(figures.map((f, i) => [f.id, i])),
    [figures],
  );

  const company = report?.company || {};
  usePortalHead(report
    ? [report.title, company.name].filter(Boolean).join(' — ')
    : t('portal.pageTitle'));

  const accent = isHex(company.primary_color) ? company.primary_color : FALLBACK_ACCENT;

  if (state === 'loading') {
    return (
      <main className="portal p-state" style={{ '--p-accent': FALLBACK_ACCENT }}>
        <p className="p-state-text" role="status">{t('portal.loading')}</p>
      </main>
    );
  }

  if (state === 'invalid' || state === 'error') {
    return (
      <main className="portal p-state" style={{ '--p-accent': FALLBACK_ACCENT }}>
        <div className="p-state-box">
          <h1 className="p-state-title">
            {state === 'invalid' ? t('portal.invalidTitle') : t('portal.errorTitle')}
          </h1>
          <p className="p-state-text">
            {state === 'invalid' ? t('portal.invalidText') : t('portal.errorText')}
          </p>
          {state === 'error' && (
            <button type="button" className="p-button" onClick={load}>{t('portal.retry')}</button>
          )}
        </div>
      </main>
    );
  }

  // The intervention (step 1 of the report); a legacy on-site client otherwise.
  const client = report.client_name
    ? { label: t('portal.intervention'), party: { name: report.client_name } }
    : report.customer
      ? { label: t('portal.customer'), party: report.customer }
      : null;

  const openFigure = (figure, event) => {
    const img = event.currentTarget.querySelector('img');
    const rect = img ? img.getBoundingClientRect() : null;
    setOpen({ index: positionOf.get(figure.id), rect });
  };

  const checked = report.checklist.filter((item) => item.checked);
  const contact = [company.address, company.phone, company.email, company.website].filter(Boolean);

  return (
    <div className="portal" style={{ '--p-accent': accent }} lang={lang}>
      <header className="p-masthead">
        <div className="p-wrap p-masthead-inner">
          {company.logo_url
            ? <img className="p-logo" src={company.logo_url} alt={company.name || ''} />
            : <span className="p-brand">{company.name}</span>}
          <a className="p-button" href={report.pdf_url} download>
            {t('portal.downloadPdf')}
          </a>
        </div>
      </header>

      <main className="p-wrap">
        <section className="p-cover" aria-labelledby="p-title">
          <p className="p-kicker">{company.header_text || t('portal.kind')}</p>
          <h1 id="p-title" className="p-title">{report.title}</h1>
          <dl className="p-meta">
            {report.reference && (
              <div><dt>{t('portal.reference')}</dt><dd>{report.reference}</dd></div>
            )}
            {report.expertise_date && (
              <div><dt>{t('portal.date')}</dt><dd>{formatDate(report.expertise_date, lang)}</dd></div>
            )}
            {report.location && (
              <div><dt>{t('portal.location')}</dt><dd>{report.location}</dd></div>
            )}
            {report.requesting_company && (
              <div><dt>{t('portal.requestedBy')}</dt><dd><Party party={report.requesting_company} /></dd></div>
            )}
            {client && (
              <div><dt>{client.label}</dt><dd><Party party={client.party} /></dd></div>
            )}
            {report.expert && (
              <div><dt>{t('portal.expert')}</dt><dd>{report.expert}</dd></div>
            )}
          </dl>
          {figures.length > 0 && (
            <p className="p-hint">{t('portal.photosHint', { count: figures.length })}</p>
          )}
        </section>

        {report.description_html && (
          <section className="p-section" aria-labelledby="p-description">
            <h2 id="p-description" className="p-h2">{t('portal.description')}</h2>
            <Rich html={report.description_html} />
          </section>
        )}

        {checked.length > 0 && (
          <section className="p-section" aria-labelledby="p-checks">
            <h2 id="p-checks" className="p-h2">{t('portal.checks')}</h2>
            <ul className="p-checks">
              {checked.map((item) => (
                <li key={item.label}>
                  <span className="p-check-label">{item.label}</span>
                  {item.text_html && <Rich html={item.text_html} />}
                </li>
              ))}
            </ul>
          </section>
        )}

        {report.steps.map((step, i) => (
          <section className="p-step" key={step.id} aria-labelledby={`p-step-${step.id}`}>
            <h2 id={`p-step-${step.id}`} className="p-step-title">
              <span className="p-step-num" aria-hidden="true">{i + 1}</span>
              <span className="visually-hidden">{t('portal.stepN', { n: i + 1 })} </span>
              {step.title || t('portal.stepN', { n: i + 1 })}
            </h2>
            {step.content_html && <Rich html={step.content_html} className="p-step-text" />}
            {step.figures.length > 0 && (
              <div className={`p-figures ${step.figures.length === 1 ? 'is-single' : ''} ${step.figures.length === 2 ? 'is-pair' : ''}`}>
                {step.figures.map((figure) => (
                  <figure className="p-figure" key={figure.id}>
                    <button
                      type="button"
                      className="p-figure-open"
                      onClick={(e) => openFigure(figure, e)}
                      aria-label={t('portal.enlarge', { n: figure.number })}
                    >
                      <img
                        src={figure.src}
                        alt={figure.caption || t('portal.photoN', { n: figure.number })}
                        loading="lazy"
                        decoding="async"
                      />
                      <span className="p-figure-zoom" aria-hidden="true">
                        <svg viewBox="0 0 24 24"><circle cx="10.5" cy="10.5" r="6" /><path d="M15 15l5 5M10.5 8v5M8 10.5h5" /></svg>
                      </span>
                    </button>
                    <figcaption>
                      <span className="p-fig-num">{t('portal.photoN', { n: figure.number })}</span>
                      {figure.caption && <span className="p-fig-text">{figure.caption}</span>}
                    </figcaption>
                  </figure>
                ))}
              </div>
            )}
          </section>
        ))}

        {report.conclusion_html && (
          <section className="p-conclusion" aria-labelledby="p-conclusion">
            <h2 id="p-conclusion" className="p-h2">{t('portal.conclusion')}</h2>
            <Rich html={report.conclusion_html} />
          </section>
        )}
      </main>

      <footer className="p-footer">
        <div className="p-wrap">
          {company.footer_text
            ? <p>{company.footer_text}</p>
            : (
              <>
                {company.name && <p className="p-footer-name">{company.name}</p>}
                {contact.length > 0 && (
                  <ul className="p-footer-contact">
                    {contact.map((c) => <li key={c}>{c}</li>)}
                  </ul>
                )}
              </>
            )}
          <p className="p-footer-note">{t('portal.privateNote')}</p>
        </div>
      </footer>

      {open && figures.length > 0 && (
        <Lightbox
          figures={figures}
          index={open.index}
          originRect={open.rect}
          onIndex={(index) => setOpen({ index, rect: null })}
          onClose={() => setOpen(null)}
        />
      )}
    </div>
  );
}
